<template>
  <BaseFilterButtons
    class="my-4"
    :filter-groups="filterGroups"
    :filter-values="filterValues"
    @filter-click="handleFilterClick"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Table } from '@/services/tableService';
import { useOperationMode } from '@/composables/useOperationMode';
import BaseFilterButtons from '@/components/common/BaseFilterButtons.vue';

type OrderStatus = 'pending' | 'preparing' | 'ready' | 'completed' | 'cancelled' | 'all';
type PaymentFilter = 'all' | 'paid' | 'unpaid';
type OrderTypeFilter = 'all' | 'dine_in' | 'takeaway' | 'delivery';
type TableFilter = number | 'all';

interface Tab {
  id: string;
}

interface Props {
  tabs: Tab[];
  selectedStatus: OrderStatus;
  paymentFilter: PaymentFilter;
  orderType: OrderTypeFilter;
  tableFilter: TableFilter;
  tables: Table[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'select-tab': [tabId: OrderStatus];
  'update:payment-filter': [value: PaymentFilter];
  'update:order-type': [value: OrderTypeFilter];
  'update:table-filter': [value: TableFilter];
}>();

const { t } = useI18n();

// Check if kitchen orders are allowed (hide filters in POS mode)
const { modeConfig } = useOperationMode();
const showFilters = computed(() => modeConfig.value?.allows_kitchen_orders ?? true);

// Status options converted to filter group format
const statusOptions = computed(() => 
  props.tabs.map(tab => ({
    value: tab.id,
    label: t('app.views.orders.tabs.' + tab.id)
  }))
);

// Payment filter options
const paymentOptions = computed(() => [
  { value: 'paid', label: t('app.views.orders.filters.paid') || 'Pagados' },
  { value: 'unpaid', label: t('app.views.orders.filters.unpaid') || 'No Pagados' }
]);

// Order type filter options
const orderTypeOptions = computed(() => [
  { value: 'dine_in', label: t('app.views.orders.filters.dine_in') || 'Comer Aquí' },
  { value: 'takeaway', label: t('app.views.orders.filters.takeaway') || 'Para Llevar' },
  { value: 'delivery', label: t('app.views.orders.filters.delivery') || 'A Domicilio' }
]);

// Table filter options
const tableOptions = computed(() => 
  props.tables.map(table => ({ value: table.id.toString(), label: table.number.toString() }))
);

// Filter groups for BaseFilterButtons
const filterGroups = computed(() => {
  const groups = [
    {
      name: 'status',
      options: statusOptions.value,
      activeClass: 'bg-indigo-600 text-white',
      allowToggle: false // Status no permite toggle en órdenes
    },
    {
      name: 'payment',
      options: paymentOptions.value,
      activeClass: 'bg-green-600 text-white',
      allowToggle: true
    },
    {
      name: 'orderType',
      options: orderTypeOptions.value,
      activeClass: 'bg-blue-600 text-white',
      allowToggle: true
    }
  ];
  
  // Add table filter if tables exist and dine_in is selected
  if (props.tables.length > 0 && props.orderType === 'dine_in') {
    groups.push({
      name: 'table',
      options: tableOptions.value,
      activeClass: 'bg-purple-600 text-white',
      allowToggle: true
    });
  }
  
  return groups;
});

// Current filter values
const filterValues = computed(() => ({
  status: props.selectedStatus,
  payment: props.paymentFilter,
  orderType: props.orderType,
  table: props.tableFilter.toString()
}));

// Event handlers
const handleFilterClick = (groupName: string, value: string | number | undefined) => {
  switch (groupName) {
    case 'status':
      emit('select-tab', (value || 'all') as OrderStatus);
      break;
    case 'payment':
      emit('update:payment-filter', (value || 'all') as PaymentFilter);
      break;
    case 'orderType':
      emit('update:order-type', (value || 'all') as OrderTypeFilter);
      break;
    case 'table':
      emit('update:table-filter', value !== undefined ? Number(value) : 'all');
      break;
  }
};
</script>

<style scoped>
/* Smooth scrolling for tabs on mobile */
.overflow-x-auto {
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* IE and Edge */
}

.overflow-x-auto::-webkit-scrollbar {
  display: none;
  /* Chrome, Safari, Opera */
}
</style>
